// ============================================================
// TANGOALPHA BT — model comparison.
//
// The honest comparison: run the strategy once, then re-value the SAME FILLS
// under the second model. The trades are held fixed — they happened, at the
// prices they happened at — so the difference is the model and nothing else.
//
// Running two independent backtests instead lets each model hedge its own
// delta, so the two runs trade differently and the whole gap gets attributed to
// "the model". Both figures are shown here, side by side, because the gap
// between them is itself the point.
// ============================================================
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { C, F } from './theme';
import { S, HEADER_H } from './ui';
import {
  BTChart, Tile, Tiles, Legend, Panel, Field,
  fullInput, fullSelect, runButton, compact, fmt, isoFromDay,
} from './btShared';

const panelPad = { padding: '12px 14px', borderBottom: `1px solid ${C.bd}` };
const rightScroll = {
  ...S.rightPanel,
  position: 'sticky',
  top: HEADER_H,
  alignSelf: 'start',
  maxHeight: `calc(100vh - ${HEADER_H}px)`,
};

const A_COLOR = C.gn;   // SABR
const B_COLOR = C.pu;   // rough SABR

export default function BTCompare({
  run, busy, error, a, b, setA, setB,
  trades, setTrades, schedule, setSchedule, hedge, setHedge,
}) {
  const base = useMemo(() => ({
    trades, schedule, hedge, roll: true,
    volHalfSpreadBp: 0.5, swapHalfSpreadBp: 0, fundingRate: 0,
  }), [trades, schedule, hedge]);

  // Run SABR first, asking the worker to re-mark the same fills under rough
  // SABR on the way through; then chain the independent rough-SABR run.
  const compare = useCallback(() => {
    run({ ...base, engine: 'sabr', remarkEngine: 'rough' }, (primary) => {
      setA(primary);
      run({ ...base, engine: 'rough' }, setB);
    });
  }, [run, base, setA, setB]);

  const ran = useRef(false);
  useEffect(() => {
    if (ran.current || a) return;
    ran.current = true;
    compare();
  }, [compare, a]);

  const markDiff = useMemo(() => {
    if (!a?.remarked) return null;
    const { days, mtm, mtmRemarked } = a.remarked;
    const diff = mtm.map((v, i) => mtmRemarked[i] - v);
    const abs = diff.map(Math.abs);
    return {
      rows: days.map((d, i) => ({ d: isoFromDay(d), diff: diff[i] })),
      mean: abs.reduce((x, y) => x + y, 0) / abs.length,
      max: Math.max(...abs),
      final: diff[diff.length - 1],
      scale: mtm.reduce((x, y) => x + Math.abs(y), 0) / mtm.length,
    };
  }, [a]);

  const pnlRows2 = useMemo(() => {
    if (!a || !b) return [];
    return a.days.map((d, i) => ({ d: isoFromDay(d), sabr: a.nav[i], rough: b.nav[i] }));
  }, [a, b]);

  return (
    <div style={S.pageContent}>
      <div style={S.leftCol}>
        {markDiff && (
          <Panel
            title="Same fills, re-marked"
            note="The identical book, valued two ways. Both models are anchored to the same ATM quote, so this is entirely a disagreement about the wings — it grows when the position drifts away from the money."
          >
            <Tiles>
              <Tile k="Mean |mark diff|" v={compact(markDiff.mean)} s="annuity units" />
              <Tile k="Max |mark diff|"  v={compact(markDiff.max)} />
              <Tile k="Final mark diff"  v={compact(markDiff.final)} />
              <Tile
                k="As % of book"
                v={`${((100 * markDiff.mean) / Math.max(markDiff.scale, 1e-12)).toFixed(2)}%`}
                s="mean |mark|"
              />
            </Tiles>
            <div style={{ marginTop: 12 }}>
              <BTChart
                rows={markDiff.rows}
                series={[{ key: 'diff', label: 'rough − SABR mark', color: C.cy, fill: true }]}
                height={230}
              />
            </div>
          </Panel>
        )}

        {a && b && (
          <>
            <Panel title="Two independent runs">
              <BTChart
                rows={pnlRows2}
                series={[
                  { key: 'sabr',  label: 'SABR',       color: A_COLOR },
                  { key: 'rough', label: 'rough SABR', color: B_COLOR },
                ]}
                height={280}
              />
              <Legend items={[
                { label: 'SABR',       color: A_COLOR, value: compact(a.metrics.totalPnl) },
                { label: 'rough SABR', color: B_COLOR, value: compact(b.metrics.totalPnl) },
              ]} />
            </Panel>

            <Panel title="Side by side">
              <SideBySide a={a} b={b} />
              <div style={{ fontSize: F.xs, color: C.txM, marginTop: 8, lineHeight: 1.55, maxWidth: '96ch' }}>
                Both runs pass the invariant checks independently
                {a.invariants.ok && b.invariants.ok
                  ? <span style={{ color: C.gn }}> ✓</span>
                  : <span style={{ color: C.rd }}> — CHECK FAILED</span>}.
                The fill counts are equal when the option schedule is the same; only the hedge
                notionals differ.
              </div>
            </Panel>
          </>
        )}

        {!a && (
          <div style={{ ...panelPad, color: C.txD, fontSize: F.md }}>
            Running SABR and rough SABR…
          </div>
        )}
      </div>

      <div style={rightScroll}>
        <div style={panelPad}>
          <div style={{ ...S.label, marginBottom: 8 }}>Strategy</div>
          <Field label="Trades">
            <textarea value={trades} onChange={(e) => setTrades(e.target.value)} rows={3}
                      style={{ ...fullInput, resize: 'vertical', lineHeight: 1.5 }} />
          </Field>
          <Field label="Entry schedule">
            <select value={schedule} onChange={(e) => setSchedule(e.target.value)} style={fullSelect}>
              <option value="one_shot">One shot</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </Field>
          <Field label="Hedge">
            <input value={hedge} onChange={(e) => setHedge(e.target.value)} style={fullInput} />
          </Field>
          <button onClick={compare} disabled={busy} style={runButton(busy)}>
            {busy ? 'RUNNING BOTH…' : 'COMPARE'}
          </button>
          <div style={{ fontSize: F.xs, marginTop: 8, color: C.rd, minHeight: 16 }}>{error || ''}</div>
        </div>

        <div style={panelPad}>
          <div style={{ ...S.label, marginBottom: 8 }}>Why two numbers</div>
          <p style={{ fontSize: F.sm, color: C.txD, lineHeight: 1.6, margin: '0 0 8px' }}>
            <strong style={{ color: C.tx }}>Re-marking</strong> holds the fills fixed and changes only
            the valuation, so the difference is purely the model.
          </p>
          <p style={{ fontSize: F.sm, color: C.txD, lineHeight: 1.6, margin: 0 }}>
            <strong style={{ color: C.tx }}>Two independent runs</strong> let each model hedge its own
            delta, so they trade differently. The gap between the two figures is the part that is about
            trading rather than pricing.
          </p>
        </div>
      </div>
    </div>
  );
}

function SideBySide({ a, b }) {
  // Sharpe is O(1) and wants decimals; everything else is a cash amount at
  // 100mm notional and wants compacting.
  const rows = [
    ['Total P&L',    a.metrics.totalPnl,       b.metrics.totalPnl,       4],
    ['Before costs', a.metrics.pnlBeforeCosts, b.metrics.pnlBeforeCosts, 4],
    ['Sharpe',       a.metrics.sharpe,         b.metrics.sharpe,         2],
    ['Ann. vol',     a.metrics.annVol,         b.metrics.annVol,         5],
    ['Max drawdown', a.metrics.maxDrawdown,    b.metrics.maxDrawdown,    4],
    ['Costs paid',   a.metrics.costsPaid,      b.metrics.costsPaid,      5],
    ['Theta P&L',    a.summary.thetaPnl,       b.summary.thetaPnl,       4],
    ['Gamma P&L',    a.summary.gammaPnl,       b.summary.gammaPnl,       4],
    ['Vega P&L',     a.summary.vegaPnl,        b.summary.vegaPnl,        4],
    ['Residual',     a.summary.residualPnl,    b.summary.residualPnl,    5],
  ];
  const f = (v, dp) => (dp <= 2 ? fmt(v, dp) : compact(v));
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${C.bd}` }}>
          <th style={{ ...S.th, textAlign: 'left' }}>Metric</th>
          <th style={{ ...S.th, textAlign: 'right', color: A_COLOR }}>SABR</th>
          <th style={{ ...S.th, textAlign: 'right', color: B_COLOR }}>Rough SABR</th>
          <th style={{ ...S.th, textAlign: 'right' }}>Difference</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, x, y, dp]) => (
          <tr key={label} style={{ borderBottom: `1px solid ${C.bd}44` }}>
            <td style={{ ...S.td, fontFamily: 'inherit', color: C.tx }}>{label}</td>
            <td style={S.tdNum}>{f(x, dp)}</td>
            <td style={S.tdNum}>{f(y, dp)}</td>
            <td style={{ ...S.tdNum, color: y - x >= 0 ? C.gn : C.rd }}>{f(y - x, dp)}</td>
          </tr>
        ))}
        <tr>
          <td style={{ ...S.td, fontFamily: 'inherit', color: C.tx }}>Fills</td>
          <td style={S.tdNum}>{a.metrics.nFills}</td>
          <td style={S.tdNum}>{b.metrics.nFills}</td>
          <td style={S.tdNum}>{b.metrics.nFills - a.metrics.nFills}</td>
        </tr>
      </tbody>
    </table>
  );
}
